import { cn } from '@planuze/ui';
import type { ReactNode } from 'react';
import { CopyableId } from './copyable-id.js';

/* Campos rótulo+valor padronizados das telas de detalhe (ADR-0699..0716). Consolida os pares
   "label:value" que cada aba montava à mão — rótulo em text-xs muted, valor em text-sm foreground,
   tratamento de vazio único ("—"), variante numérica (tabular-nums) e afford. de cópia/badge.
   Reusa o token approach de SummaryMetrics e o CopyableId já existentes. */

/* Placeholder consistente de valor ausente em qualquer campo de detalhe — fonte única do traço
   para não divergir entre abas (bloco rótulo+valor ou célula de tabela). */
export const EMPTY_VALUE = '—';

/* Vazio = sem conteúdo a exibir: null/undefined/string vazia caem no placeholder. `0` e `false`
   são valores legítimos (contagens/flags) e nunca são tratados como vazios. */
function isEmpty(value: ReactNode): boolean {
  return value === null || value === undefined || value === '';
}

interface FieldProps {
  readonly label: string;
  /* Valor renderizado. null/undefined/'' cai no placeholder consistente. Aceita ReactNode
     (StatusBadge, link, texto) além de string. */
  readonly value?: ReactNode;
  /* Alinha dígitos de datas/contagens/versões com tabular-nums. */
  readonly numeric?: boolean;
  /* Renderiza o valor como identificador técnico copiável (mono + botão copiar). Espera `value`
     string; `numeric` é ignorado. */
  readonly copyable?: boolean;
  /* Rótulo curto do que se copia (aria-label/toast do CopyableId). */
  readonly copyLabel?: string;
  /* Ocupa a linha inteira numa grade multi-coluna (ex.: um id longo). */
  readonly full?: boolean;
}

/* Um par rótulo+valor de tela de detalhe. */
export function Field({
  label,
  value,
  numeric = false,
  copyable = false,
  copyLabel,
  full = false,
}: FieldProps) {
  const empty = isEmpty(value);
  return (
    <div className={cn(full && 'col-span-full')}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'text-sm',
          empty ? 'text-muted-foreground' : 'text-foreground',
          numeric && 'tabular-nums',
        )}
      >
        {empty ? (
          EMPTY_VALUE
        ) : copyable ? (
          <CopyableId
            value={String(value)}
            {...(copyLabel !== undefined && { label: copyLabel })}
          />
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/* Colunas no breakpoint sm+ — uma coluna para superfícies estreitas (painel/sheet). */
const COLUMNS_CLASS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
} as const;

interface DefinitionListProps {
  readonly children: ReactNode;
  readonly columns?: 1 | 2 | 3;
  readonly className?: string;
}

/* Grade semântica (<dl>) de campos rótulo+valor. */
export function DefinitionList({ children, columns = 2, className }: DefinitionListProps) {
  return (
    <dl className={cn('grid gap-x-8 gap-y-4', COLUMNS_CLASS[columns], className)}>{children}</dl>
  );
}
